import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { ContentPage } from "@/components/ContentPage";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import {
  SpecComparisonTable,
  type SpecColumn,
  type SpecRow,
} from "@/components/SpecComparisonTable";
import { IMAGES } from "@/lib/images";

/**
 * Bredare jämförelse än den befintliga guiden /guider/dekorfilm-frostat-glas
 * (som fokuserar på hur dekorfilm används). Här ställs fem insynsskydds­alternativ
 * mot varandra med fokus på kostnad över tid, flexibilitet och regelefterlevnad.
 */
const COLS: SpecColumn[] = [
  { id: "frost", label: "Frostfilm", highlight: true },
  { id: "dekor", label: "Dekorfilm (tryck/logotyp)" },
  { id: "sand", label: "Sandblästrat glas" },
  { id: "ets", label: "Etsat glas" },
  { id: "pers", label: "Persienner" },
];

const ROWS: SpecRow[] = [
  {
    feature: "Insynsskydd",
    values: {
      frost: "100 %, diffust ljus släpps igenom",
      dekor: "Anpassningsbart — heltäckande eller mönstrat",
      sand: "100 %, diffust ljus släpps igenom",
      ets: "100 %, diffust ljus släpps igenom",
      pers: "0–100 %, manuellt reglerbart",
    },
    source: "Tillverkar­specifikationer + branschpraxis",
  },
  {
    feature: "Släpper igenom dagsljus",
    values: {
      frost: "Ja — diffust",
      dekor: "Ja, där filmen är genomskinlig",
      sand: "Ja — diffust",
      ets: "Ja — diffust",
      pers: "Endast när öppna",
    },
    source: "Tillverkar­specifikationer",
  },
  {
    feature: "Reversibel / kan tas bort utan spår",
    values: {
      frost: "Ja",
      dekor: "Ja",
      sand: "Nej — permanent",
      ets: "Nej — permanent",
      pers: "Ja (demonteras)",
    },
    source: "Branschpraxis",
  },
  {
    feature: "Logotyp / branding möjlig",
    values: {
      frost: "Ja — plottas i mönster eller logotyp",
      dekor: "Ja — kärnanvändning",
      sand: "Ja, men mycket dyrt",
      ets: "Ja, men mycket dyrt och permanent",
      pers: "Nej",
    },
    source: "/dekorfilm + /guider/dekorfilm-frostat-glas",
  },
  {
    feature: "Initial kostnad per kvm (installerat, indikativt)",
    values: {
      frost: "Låg–mellan",
      dekor: "Låg–mellan",
      sand: "Hög (kräver demontering av glas)",
      ets: "Mycket hög",
      pers: "Mellan (plus motorisering vid el-drift)",
    },
    source: "BBN Nordic prisspann 2026",
  },
  {
    feature: "Förväntad livslängd inomhus",
    values: {
      frost: "10+ år",
      dekor: "7–10 år (färgad/tryckt)",
      sand: "Permanent",
      ets: "Permanent",
      pers: "5–10 år (mekanik slits, lameller skadas)",
    },
    source: "Tillverkargarantier + /guider/dekorfilm-frostat-glas",
  },
  {
    feature: "Underhåll över tid",
    values: {
      frost: "Torkas av som glas — inget mekaniskt slitage",
      dekor: "Torkas av som glas",
      sand: "Som glas — kan dock bli svårare att rengöra i frostytan",
      ets: "Som glas",
      pers: "Dammsugning, justering av lameller, byte av snören/motor",
    },
    source: "Branschpraxis",
  },
  {
    feature: "Ungefärlig kostnad över 10 år",
    values: {
      frost: "Engångskostnad — inget underhåll",
      dekor: "Engångskostnad + eventuellt designbyte",
      sand: "Hög engångskostnad — permanent",
      ets: "Mycket hög engångskostnad — permanent",
      pers: "Engångskostnad + underhåll + ev. byte av mekanik",
    },
    source: "BBN Nordic 10-årig TCO-modell",
  },
  {
    feature: "Påverkar fönstrets U-värde / fasaden",
    values: {
      frost: "Nej",
      dekor: "Nej",
      sand: "Ja — glaset bearbetas och tjockleken kan påverkas",
      ets: "Ja — glaset byts/bearbetas",
      pers: "Nej (men kan påverka solinstrålning)",
    },
    source: "Glasleverantörens produktblad",
  },
  {
    feature: "Bygglov / fastighetsägar­godkännande",
    values: {
      frost: "Sällan — film på insidan",
      dekor: "Sällan",
      sand: "Ofta — glasbyte krävs",
      ets: "Ofta — glasbyte krävs",
      pers: "Sällan",
    },
    source: "Branschpraxis BRF/hyresfastighet",
  },
  {
    feature: "AFS-markering på glasdörrar",
    values: {
      frost: "Uppfyller kravet — standardlösning",
      dekor: "Uppfyller kravet",
      sand: "Uppfyller kravet",
      ets: "Uppfyller kravet",
      pers: "Nej — markering måste lösas separat",
    },
    source: "AFS 2020:1 + /guider/dekorfilm-frostat-glas",
  },
  {
    feature: "Kombinerbart med solfilm",
    values: {
      frost: "Ja",
      dekor: "Ja",
      sand: "Sällan / problematiskt",
      ets: "Sällan / problematiskt",
      pers: "Ja",
    },
    source: "Tillverkar­rekommendation",
  },
  {
    feature: "Bäst för",
    values: {
      frost: "Mötesrum, badrum, kontor, BRF, hyrda lokaler",
      dekor: "Branding, kampanjer, logotyp, fönsterkonst",
      sand: "Nybyggnation med statisk design och budget",
      ets: "Premium-nybyggnation, representations­miljöer",
      pers: "Rum där full ljusreglering krävs (sovrum, konferens med AV-utrustning)",
    },
    source: "Branschpraxis + /dekorfilm",
  },
];

export const FAQS: FaqItem[] = [
  {
    q: "Vad är skillnaden mellan frostfilm, sandblästrat glas och etsat glas?",
    a: "Frostfilm går att ta bort, medan sandblästring och etsning förändrar glaset permanent. Alla tre ger ett liknande mjölkvitt utseende som dämpar insyn. Frostfilm är en självhäftande satinfilm som monteras på befintligt glas och kan tas bort utan spår. Sandblästring innebär att glaset demonteras och ytan bearbetas mekaniskt med slipsand, vilket är permanent. Etsat glas bearbetas kemiskt eller säljs som färdigt etsat glas vid byte av ruta. Även det är permanent. Kostnadsmässigt är frostfilm typiskt 60–80 % billigare än sand- eller etsbearbetning eftersom inget glas behöver bytas.",
  },
  {
    q: "När är persienner bättre än frostfilm för insynsskydd?",
    a: "Persienner passar bättre när du vill kunna reglera insyn och ljus, till exempel i sovrum, konferensrum med projektor eller rum där du ibland vill ha fri sikt och ibland skärma av helt. Frostfilm ger konstant 100 % insynsskydd och diffust ljus; persienner ger 0–100 % beroende på position. För mötesrum, badrum, glasväggar och kontor där insynsskyddet ska vara permanent är frostfilm billigare i drift och kräver inget underhåll.",
  },
  {
    q: "Vilket alternativ ger lägst totalkostnad över 10 år?",
    a: "Frostfilm ger normalt lägst totalkostnad över 10 år. Den har låg inköpskostnad, håller 10+ år inomhus och har ingen underhållskostnad utöver att torkas av som vanligt glas. Persienner har lägre eller jämförbar initialkostnad men slits mekaniskt (snören, lameller, motorer) och kräver typiskt byte inom 5–10 år. Sandblästrat och etsat glas har mycket hög initialkostnad men permanent hållbarhet. Totalkostnaden över 10 år blir ändå högre eftersom engångskostnaden är så mycket större.",
  },
  {
    q: "Får man montera frostfilm i en hyrd lokal eller bostadsrätt?",
    a: "Ja, normalt utan särskilt godkännande eftersom filmen sitter på glasets insida och kan tas bort utan spår vid avflyttning. Sandblästrat och etsat glas innebär bearbetning av fastighetsägarens glas och kräver oftast hyresvärdens eller föreningens skriftliga godkännande — och kan kräva återställning vid avflyttning, vilket innebär byte av hela rutan. Det är en av huvudanledningarna till att frostfilm är standardvalet i hyrda kontor och BRF-lokaler.",
  },
  {
    q: "Uppfyller frostfilm AFS-kravet på markering av glasdörrar?",
    a: "Ja, frostfilm kan användas för att uppfylla kravet. AFS 2020:1 kräver att glaspartier och glasdörrar i arbetsmiljö ska markeras så att personer inte går rakt in i glaset. Plottade frostränder eller logotyp i ögonhöjd uppfyller kravet. Standardhöjden är 80–120 cm hög med underkant cirka 90 cm från golv. Sandblästrat och etsat glas uppfyller också kravet men löser problemet permanent och dyrare. Persienner uppfyller inte kravet eftersom de kan vara öppna.",
  },
  {
    q: "Kan man ändra designen efter installation?",
    a: "Ja, frostfilm och dekorfilm kan tas bort och ersättas när du vill byta logotyp, mönster eller företagsfärger. Sandblästrat och etsat glas måste däremot bytas ut. Skillnaden spelar roll för företag som ändrar varumärke, BRF:er som vill prova en design och butiker som byter kampanjer.",
  },
];

export const Route = createFileRoute("/frostat-glas-vs-dekorfolie")({
  head: () => ({
    meta: [
      {
        title:
          "Frostfilm vs sandblästrat glas vs etsat glas vs persienner — komplett jämförelse | BBN Nordic",
      },
      {
        name: "description",
        content:
          "Fem insynsskydds­alternativ jämförda — kostnad, livslängd, flexibilitet och totalkostnad över 10 år. Frostfilm, dekorfilm, sandblästrat och etsat glas samt persienner.",
      },
      {
        property: "og:title",
        content:
          "Frostfilm vs sandblästrat vs etsat glas vs persienner — komplett jämförelse",
      },
      {
        property: "og:description",
        content:
          "Vilket insynsskydd ger lägst totalkostnad över 10 år? Källhänvisad jämförelse från auktoriserad installatör.",
      },
      { property: "og:url", content: "https://bbnnordic.se/frostat-glas-vs-dekorfolie" },
      { property: "og:type", content: "article" },
      { property: "og:image", content: IMAGES.dekorfolie },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/frostat-glas-vs-dekorfolie" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQS)) },
    ],
  }),
  component: ComparePage,
});

function ComparePage() {
  return (
    <ContentPage
      eyebrow="JÄMFÖRELSE — INSYNSSKYDD"
      title="Frostfilm vs sandblästrat, etsat glas och persienner"
      subtitle="Här jämför vi fem insynsskydd för glasväggar, mötesrum och fönster. Du kan se skillnader i pris, livslängd, möjlighet att ändra och totalkostnad över 10 år."
      heroImage={IMAGES.dekorfolie}
      breadcrumbs={[{ label: "Frostfilm vs alternativ" }]}
      priorityContent={
        <section className="py-16 md:py-20 bg-cream">
          <div className="mx-auto max-w-7xl px-5 md:px-10 space-y-6">
            <p className="text-sm text-muted-foreground max-w-3xl">
              Den här sidan jämför insynsskydds­alternativ sida vid sida. För
              detaljerad genomgång av just dekor- och frostfilm — hur den används,
              standardhöjder, monteringsprocess — se den fördjupande guiden{" "}
              <Link
                to="/guider/dekorfilm-frostat-glas"
                className="underline decoration-gold/40 hover:text-foreground"
              >
                Dekorfilm & frostat glas
              </Link>
              .
            </p>

            <SpecComparisonTable
              columns={COLS}
              rows={ROWS}
              caption="Jämförelse av fem insynsskydds­alternativ: frostfilm, dekorfilm, sandblästrat glas, etsat glas och persienner — med källhänvisning per rad."
              tone="light"
            />

            <p className="text-xs text-muted-foreground italic max-w-3xl">
              Indikativa värden från BBN Nordics 10-åriga TCO-modell och respektive
              tillverkares produktblad. Exakta priser kräver platsbesök eftersom
              glasstorlek, åtkomst och eventuell demontering påverkar.
            </p>

            <Link
              to="/dekorfilm"
              className="inline-flex items-center gap-2 bg-navy text-cream px-6 py-3 rounded-full font-semibold hover:gap-3 transition-all"
            >
              Begär offert på frost- eller dekorfilm <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      }
      sections={[
        {
          eyebrow: "SLUTSATS",
          heading: "Därför väljer vi oftast frostfilm.",
          paragraphs: [
            "Frostfilm ger lägst totalkostnad över 10 år. Den kostar en bråkdel av sandblästrat eller etsat glas att montera, håller i 10+ år och har ingen underhållskostnad. Persienner är ofta billigare att köpa men har mekaniska delar som slits och behöver bytas.",
            "Du kan ta bort filmen utan spår. Därför är den ett vanligt val i hyrda lokaler, BRF och företag som vill kunna ändra design eller varumärke längre fram.",
            "Frostränder eller en logotyp i ögonhöjd uppfyller AFS 2020:1. Det är en standardlösning för arbetsmiljöansvariga som behöver markera glasdörrar och glaspartier i passager.",
            "Vill du kunna reglera ljus och insyn i ett sovrum eller projektor­rum passar persienner bättre. Vid nybyggnation där designen ska vara permanent och budgeten tillåter en dyrare ytbehandling kan etsat glas vara värt den högre engångs­kostnaden.",
          ],
        },
      ]}
      related={[
        { to: "/dekorfilm", label: "Dekorfilm — översikt & offert", desc: "Frost, plottad logotyp, kulörfilm — sortiment och installation." },
        { to: "/guider/dekorfilm-frostat-glas", label: "Guide: Dekorfilm & frostat glas", desc: "Fördjupande genomgång av användningsområden, standardhöjder och process." },
        { to: "/sakerhetsfilm", label: "Säkerhetsfilm", desc: "Splitterskydd och inbrottsfördröjning — kan kombineras med frostfilm." },
      ]}
    >
      <FaqSection items={FAQS} heading="Vanliga frågor — frostfilm vs alternativ" />
    </ContentPage>
  );
}
